import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaCheckCircle } from 'react-icons/fa';
import type { Stripe, StripeCardElement } from '@stripe/stripe-js';
import type { BillingSummary } from '../client';
import { useApi } from '../state/ApiContext';
import Modal from '../components/Modal';
import AsyncButton from '../components/AsyncButton';
import { useGuardedCallback } from '../permissions/useGuardedAction';
import {
  PermissionLevel,
  PermissionResource,
} from '../permissions/permission-catalog';
import { formatCostTotal } from '../utils/formatCost';
import {
  MockCardForm,
  StripeCardForm,
  cardLabel,
  useStripeLoader,
} from './StripeCardForm';
import DollarAmountInput from './DollarAmountInput';
import { MIN_CARD_CHARGE_USD } from './billing-constants';
import CreditCodeRedemptionForm, {
  type CreditRedemption,
} from './CreditCodeRedemptionForm';

interface AddFundsModalProps {
  teamId: string;
  isOpen: boolean;
  onClose: () => void;
  /**
   * Called after funds are successfully added. Card flows provide the fresh
   * billing summary they already fetched; credit flows omit it.
   */
  onFundsAdded?: (summary?: BillingSummary) => void;
  /**
   * Opens the spending-limit increase flow. Offered inline when a charge is
   * refused by the 30-day limit, so the fix is one click away instead of a
   * hunt across the billing page.
   */
  onRequestLimitIncrease?: () => void;
}

/** The stable copy formatChargeCapExceededMessage emits on a cap refusal. */
const CHARGE_LIMIT_ERROR_MARKER = 'would exceed the 30-day limit';

const DEFAULT_AMOUNT = '25.00';
const DEFAULT_AUTORENEW_MIN = '10';
const DEFAULT_AUTORENEW_AMOUNT = '25';
const MAX_AUTORENEW_USD = 10_000;
const PAYMENT_RECOVERY_TTL_MS = 30 * 60 * 1000;

type Step = 'payment' | 'options';
type FundingMethod = 'card' | 'credit';
type CardOperationMode = 'saved_card' | 'one_off' | 'one_off_save';
type AutoRenewAction =
  | { enabled: false }
  | { enabled: true; minBalanceUsd: number; amountUsd: number };

interface StoredAddFundsOperation {
  operationId: string;
  teamId: number;
  expiresAt: number;
  amountUsd: number;
  mode: CardOperationMode;
  paymentId?: number;
  clientConfirmed?: boolean;
  autoRenew?: AutoRenewAction;
}

const operationStorageKey = (teamId: string): string | null => {
  const parsedTeamId = Number(teamId);
  return Number.isSafeInteger(parsedTeamId) && parsedTeamId > 0
    ? `delegate.billing.operation.${parsedTeamId}`
    : null;
};

function removeStoredOperation(storageKey: string): void {
  try {
    window.sessionStorage.removeItem(storageKey);
  } catch {
    // A blocked storage area already cannot retain recovery state.
  }
}

const hasWholeUsdMinorUnits = (value: number): boolean =>
  Number.isFinite(value) && /^\d+(?:\.\d{1,2})?$/.test(value.toString());

function isValidAutoRenewSettings(value: unknown): value is {
  minBalanceUsd: number;
  amountUsd: number;
} {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const settings = value as {
    minBalanceUsd?: unknown;
    amountUsd?: unknown;
  };
  return (
    typeof settings.minBalanceUsd === 'number' &&
    hasWholeUsdMinorUnits(settings.minBalanceUsd) &&
    settings.minBalanceUsd >= 0 &&
    settings.minBalanceUsd <= MAX_AUTORENEW_USD &&
    typeof settings.amountUsd === 'number' &&
    hasWholeUsdMinorUnits(settings.amountUsd) &&
    settings.amountUsd > 0 &&
    settings.amountUsd <= MAX_AUTORENEW_USD
  );
}

function normalizeStoredAutoRenewAction(
  value: unknown,
): AutoRenewAction | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const settings = value as {
    enabled?: unknown;
    minBalanceUsd?: unknown;
    amountUsd?: unknown;
  };
  if (settings.enabled === false) return { enabled: false };
  // Operations persisted by the preceding release omitted `enabled`; their
  // settings object unambiguously represented an enable action.
  if (
    (settings.enabled === true || settings.enabled === undefined) &&
    isValidAutoRenewSettings(settings)
  ) {
    return {
      enabled: true,
      minBalanceUsd: settings.minBalanceUsd,
      amountUsd: settings.amountUsd,
    };
  }
  return null;
}

function readStoredOperation(teamId: string): StoredAddFundsOperation | null {
  const storageKey = operationStorageKey(teamId);
  if (!storageKey) return null;
  const expectedTeamId = Number(teamId);
  const now = Date.now();
  try {
    const raw = window.sessionStorage.getItem(storageKey);
    if (!raw) return null;
    const value = JSON.parse(raw) as Partial<StoredAddFundsOperation>;
    if (
      typeof value.operationId !== 'string' ||
      !/^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
        value.operationId,
      ) ||
      value.teamId !== expectedTeamId ||
      typeof value.expiresAt !== 'number' ||
      !Number.isFinite(value.expiresAt) ||
      value.expiresAt <= now ||
      value.expiresAt > now + PAYMENT_RECOVERY_TTL_MS ||
      typeof value.amountUsd !== 'number' ||
      !hasWholeUsdMinorUnits(value.amountUsd) ||
      value.amountUsd <= 0 ||
      value.amountUsd > MAX_AUTORENEW_USD ||
      !['saved_card', 'one_off', 'one_off_save'].includes(value.mode ?? '') ||
      (value.paymentId !== undefined &&
        (!Number.isSafeInteger(value.paymentId) || value.paymentId <= 0)) ||
      (value.clientConfirmed !== undefined &&
        typeof value.clientConfirmed !== 'boolean')
    ) {
      removeStoredOperation(storageKey);
      return null;
    }
    const operation: StoredAddFundsOperation = {
      operationId: value.operationId,
      teamId: value.teamId,
      expiresAt: value.expiresAt,
      amountUsd: value.amountUsd,
      mode: value.mode as CardOperationMode,
      ...(value.paymentId !== undefined ? { paymentId: value.paymentId } : {}),
      ...(value.clientConfirmed !== undefined
        ? { clientConfirmed: value.clientConfirmed }
        : {}),
    };
    const autoRenewAction = normalizeStoredAutoRenewAction(value.autoRenew);
    if (
      value.autoRenew !== undefined &&
      value.mode !== 'one_off' &&
      autoRenewAction
    ) {
      operation.autoRenew = autoRenewAction;
    } else if (value.autoRenew !== undefined) {
      // The payment UUID/tuple may already represent a successful charge.
      // Remove only unsafe post-payment settings so a reload can replay that
      // same operation and let the user correct or leave auto-renew disabled.
      try {
        window.sessionStorage.setItem(storageKey, JSON.stringify(operation));
      } catch {
        // The in-memory operation remains safe for this session. A storage
        // failure is handled by the next normal persistence attempt.
      }
    }
    return operation;
  } catch {
    removeStoredOperation(storageKey);
    return null;
  }
}

function newOperationId(): string {
  if (typeof window.crypto?.randomUUID === 'function') {
    return window.crypto.randomUUID();
  }
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (value) => value.toString(16).padStart(2, '0'));
  return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex.slice(6, 8).join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10).join('')}`;
}

interface EnteredCard {
  stripe: Stripe;
  card: StripeCardElement;
}

/**
 * Add funds to the team balance from inside a modal — no page switch. Step 1
 * collects the amount and a card; step 2 offers "save card" and "auto-renew"
 * options and confirms the charge. Mirrors the billing-page flows
 * (`TeamBilling.tsx`) but packaged so it can be launched from the chat
 * composer's funds notice. On success it fires `onFundsAdded` so the notice
 * can re-check billing and disappear.
 */
const AddFundsModal: React.FC<AddFundsModalProps> = ({
  teamId,
  isOpen,
  onClose,
  onFundsAdded,
  onRequestLimitIncrease,
}) => {
  const teamIdNum = parseInt(teamId, 10);
  const { billingApi, teamApi } = useApi();

  const [summary, setSummary] = useState<BillingSummary | null>(null);
  const [teamName, setTeamName] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [step, setStep] = useState<Step>('payment');
  const [method, setMethod] = useState<FundingMethod>('card');
  const [amount, setAmount] = useState(DEFAULT_AMOUNT);
  const [useNewCard, setUseNewCard] = useState(true);
  const enteredCardRef = useRef<EnteredCard | null>(null);

  const [saveCard, setSaveCard] = useState(false);
  const [autoRenew, setAutoRenew] = useState(false);
  const [autoRenewMin, setAutoRenewMin] = useState(DEFAULT_AUTORENEW_MIN);
  const [autoRenewAmount, setAutoRenewAmount] = useState(
    DEFAULT_AUTORENEW_AMOUNT,
  );

  const [busy, setBusy] = useState(false);
  const [creditBusy, setCreditBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cardOperation, setCardOperation] =
    useState<StoredAddFundsOperation | null>(null);
  const cardOperationRef = useRef<StoredAddFundsOperation | null>(null);
  const [success, setSuccess] = useState<{
    amountUsd: number;
    balanceUsd: number | null;
  } | null>(null);
  const sessionGenerationRef = useRef(0);

  const persistCardOperation = (next: StoredAddFundsOperation | null) => {
    cardOperationRef.current = next;
    setCardOperation(next);
    const storageKey = operationStorageKey(teamId);
    if (storageKey) {
      try {
        if (next) {
          window.sessionStorage.setItem(storageKey, JSON.stringify(next));
        } else {
          window.sessionStorage.removeItem(storageKey);
        }
      } catch {
        // The in-memory operation remains authoritative for this modal mount.
      }
    }
  };

  const provider = summary?.provider;
  const isStripe = provider === 'stripe';
  const publishableKey = summary?.publishableKey || '';
  const getStripe = useStripeLoader(publishableKey);
  const savedCard = summary?.paymentMethod;
  const hasSavedCard = !!savedCard;
  const usingSavedCard = hasSavedCard && !useNewCard;
  // Backend-owned minimum card charge; fall back until the summary loads.
  const minChargeUsd = summary?.minChargeUsd ?? MIN_CARD_CHARGE_USD;

  // Invalidate async card work before paint whenever the modal session changes.
  // Old server-side charge continuations may finish, but cannot update this
  // session's UI or invoke its completion callback.
  useLayoutEffect(() => {
    sessionGenerationRef.current += 1;
    if (isOpen) {
      const restored = readStoredOperation(teamId);
      cardOperationRef.current = restored;
      setCardOperation(restored);
      setSummary(null);
      setLoadError(null);
      setBusy(false);
      setCreditBusy(false);
      setStep(restored ? 'options' : 'payment');
      setMethod('card');
      setAmount(restored ? restored.amountUsd.toFixed(2) : DEFAULT_AMOUNT);
      setUseNewCard(restored?.mode !== 'saved_card');
      setSaveCard(restored?.mode === 'one_off_save');
      setAutoRenew(restored?.autoRenew?.enabled === true);
      setAutoRenewMin(
        restored?.autoRenew?.enabled === true
          ? restored.autoRenew.minBalanceUsd.toString()
          : DEFAULT_AUTORENEW_MIN,
      );
      setAutoRenewAmount(
        restored?.autoRenew?.enabled === true
          ? restored.autoRenew.amountUsd.toString()
          : DEFAULT_AUTORENEW_AMOUNT,
      );
      setError(null);
      setSuccess(null);
      setTeamName(null);
      enteredCardRef.current = null;
    }
    return () => {
      sessionGenerationRef.current += 1;
    };
  }, [isOpen, billingApi, teamIdNum]);

  const isCurrentSession = (generation: number) =>
    sessionGenerationRef.current === generation;

  // Fetch the summary (provider/key/saved card) each time the modal opens.
  useEffect(() => {
    if (!isOpen) return;
    if (Number.isNaN(teamIdNum) || !billingApi?.apiTeamTeamIdBillingGet) return;
    let cancelled = false;
    Promise.resolve(billingApi.apiTeamTeamIdBillingGet(teamIdNum))
      .then((res) => {
        if (cancelled) return;
        setSummary(res.data);
        const restored = readStoredOperation(teamId);
        setUseNewCard(
          restored ? restored.mode !== 'saved_card' : !res.data.paymentMethod,
        );
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to load billing for add-funds modal:', err);
        setLoadError('Failed to load payment settings');
      });
    // Team name (for the "funds will be added to …" line). Best-effort.
    if (teamApi?.apiTeamsGet) {
      Promise.resolve(teamApi.apiTeamsGet())
        .then((res) => {
          if (cancelled) return;
          const team = (res.data || []).find(
            (t: { id?: number }) => t.id === teamIdNum,
          );
          if (team?.name) setTeamName(team.name);
        })
        .catch(() => undefined);
    }
    return () => {
      cancelled = true;
    };
  }, [isOpen, billingApi, teamApi, teamIdNum]);

  const parsedAmount = (): number | null => {
    const n = parseFloat(amount);
    if (!hasWholeUsdMinorUnits(n) || n < minChargeUsd || n > 10000) return null;
    return n;
  };

  const amountError = `Enter an amount between $${minChargeUsd} and $10,000`;

  const advanceToOptions = () => {
    if (parsedAmount() === null) {
      setError(amountError);
      return;
    }
    setError(null);
    setStep('options');
  };

  // Step 1 "Continue" for a newly entered Stripe card: capture it, then advance.
  const onNewCardContinue = (stripe: Stripe, card: StripeCardElement) => {
    enteredCardRef.current = { stripe, card };
    advanceToOptions();
  };

  // --- charge helpers: each returns false (and sets error) on failure -----

  const chargeSavedCard = async (
    amt: number,
    sessionGeneration: number,
    operation: StoredAddFundsOperation,
  ): Promise<boolean> => {
    const res = await billingApi.apiTeamTeamIdBillingPrepayPost(teamIdNum, {
      amountUsd: amt,
      operationId: operation.operationId,
      currency: 'usd',
      useSavedCard: true,
    });
    if (!isCurrentSession(sessionGeneration)) return false;
    if (res.data.status === 'succeeded') {
      return true;
    }
    if (res.data.status === 'failed') {
      persistCardOperation(null);
      if (isCurrentSession(sessionGeneration)) {
        const providerMessage =
          'errorMessage' in res.data &&
          typeof res.data.errorMessage === 'string'
            ? res.data.errorMessage
            : undefined;
        setError(providerMessage || 'Payment failed');
      }
      return false;
    }
    persistCardOperation({ ...operation, paymentId: res.data.paymentId });
    if (isCurrentSession(sessionGeneration)) {
      setError(null);
    }
    return false;
  };

  const chargeOneOff = async (
    amt: number,
    entered: EnteredCard | null,
    sessionGeneration: number,
    operation: StoredAddFundsOperation,
  ): Promise<boolean> => {
    const res = await billingApi.apiTeamTeamIdBillingPrepayPost(teamIdNum, {
      amountUsd: amt,
      operationId: operation.operationId,
      currency: 'usd',
      useSavedCard: false,
      savePaymentMethod: operation.mode === 'one_off_save',
    });
    if (!isCurrentSession(sessionGeneration)) return false;
    const { paymentId, status, clientSecret } = res.data;
    if (status === 'succeeded') {
      return true;
    }
    if (status !== 'pending') {
      persistCardOperation(null);
      if (isCurrentSession(sessionGeneration)) {
        setError(res.data.errorMessage || `Payment ${status}`);
      }
      return false;
    }
    let pendingOperation: StoredAddFundsOperation = { ...operation, paymentId };
    persistCardOperation(pendingOperation);
    if (isStripe && !pendingOperation.clientConfirmed) {
      if (!entered || !clientSecret) {
        if (isCurrentSession(sessionGeneration)) {
          setError('Enter your card details first');
          setStep('payment');
        }
        return false;
      }
      const result = await entered.stripe.confirmCardPayment(clientSecret, {
        payment_method: { card: entered.card },
      });
      if (!isCurrentSession(sessionGeneration)) return false;
      if (result.error) {
        setError(result.error.message || 'Payment failed');
        return false;
      }
      pendingOperation = { ...pendingOperation, clientConfirmed: true };
      persistCardOperation(pendingOperation);
    } else if (!isStripe && !pendingOperation.clientConfirmed) {
      pendingOperation = { ...pendingOperation, clientConfirmed: true };
      persistCardOperation(pendingOperation);
    }
    // Mock provider settles server-side; both providers confirm here. The
    // charge can still be declined at this step (e.g. the intent fails), so
    // honor the confirm status rather than assuming success.
    const confirmRes =
      await billingApi.apiTeamTeamIdBillingPrepayPaymentIdConfirmPost(
        teamIdNum,
        paymentId,
        { operationId: operation.operationId },
      );
    if (!isCurrentSession(sessionGeneration)) return false;
    if (confirmRes.data.status === 'succeeded') {
      return true;
    }
    if (confirmRes.data.status === 'failed') {
      persistCardOperation(null);
      if (isCurrentSession(sessionGeneration)) {
        setError('Your card was declined.');
      }
      return false;
    }
    if (isCurrentSession(sessionGeneration)) {
      setError(null);
    }
    return false;
  };

  const showSuccess = async (
    amt: number,
    sessionGeneration: number,
  ): Promise<BillingSummary | undefined> => {
    const res = await billingApi.apiTeamTeamIdBillingGet(teamIdNum);
    if (!isCurrentSession(sessionGeneration)) return undefined;
    setSuccess({ amountUsd: amt, balanceUsd: res.data.balanceUsd });
    return res.data;
  };

  /** Every write here is `billing: manage` — it moves money. */
  const billingGuard = useGuardedCallback<[action: () => void]>({
    resource: PermissionResource.BILLING,
    level: PermissionLevel.MANAGE,
    verb: 'change',
    subject: "the team's billing",
    testId: 'billing-denied',
    onAllowed: (action) => action(),
  });

  const submit = async () => {
    const sessionGeneration = sessionGenerationRef.current;
    const existingOperation = cardOperationRef.current;
    const amt = existingOperation?.amountUsd ?? parsedAmount();
    if (amt === null) {
      setError(amountError);
      setStep('payment');
      return;
    }
    const persistCard = !usingSavedCard && (saveCard || autoRenew);
    let arMin = 0;
    let arAmt = 0;
    if (autoRenew) {
      arMin = Number(autoRenewMin);
      arAmt = Number(autoRenewAmount);
      if (
        autoRenewMin.trim() === '' ||
        autoRenewAmount.trim() === '' ||
        !isValidAutoRenewSettings({
          minBalanceUsd: arMin,
          amountUsd: arAmt,
        })
      ) {
        setError(
          'Auto-renew values must use exact cents and be no more than $10,000',
        );
        return;
      }
    }

    const requestedMode: CardOperationMode =
      existingOperation?.mode ??
      (usingSavedCard
        ? 'saved_card'
        : persistCard
          ? 'one_off_save'
          : 'one_off');
    if (
      existingOperation &&
      (existingOperation.amountUsd !== amt ||
        existingOperation.mode !== requestedMode)
    ) {
      setError(
        'Finish the payment already in progress before starting a different one.',
      );
      return;
    }
    const operation: StoredAddFundsOperation = {
      operationId: existingOperation?.operationId ?? newOperationId(),
      teamId: existingOperation?.teamId ?? teamIdNum,
      expiresAt:
        existingOperation?.expiresAt ?? Date.now() + PAYMENT_RECOVERY_TTL_MS,
      amountUsd: existingOperation?.amountUsd ?? amt,
      mode: existingOperation?.mode ?? requestedMode,
      ...(existingOperation?.paymentId !== undefined
        ? { paymentId: existingOperation.paymentId }
        : {}),
      ...(existingOperation?.clientConfirmed !== undefined
        ? { clientConfirmed: existingOperation.clientConfirmed }
        : {}),
      ...(autoRenew
        ? {
            autoRenew: {
              enabled: true as const,
              minBalanceUsd: arMin,
              amountUsd: arAmt,
            },
          }
        : existingOperation?.autoRenew
          ? { autoRenew: { enabled: false as const } }
          : {}),
    };
    persistCardOperation(operation);

    setBusy(true);
    setError(null);
    try {
      const entered = enteredCardRef.current;
      let ok: boolean;
      if (operation.mode === 'saved_card') {
        ok = await chargeSavedCard(amt, sessionGeneration, operation);
      } else {
        ok = await chargeOneOff(amt, entered, sessionGeneration, operation);
      }
      if (!ok) return;

      if (operation.autoRenew) {
        await billingApi.apiTeamTeamIdBillingAutoRenewPut(
          teamIdNum,
          operation.autoRenew.enabled
            ? {
                enabled: true,
                minBalanceUsd: operation.autoRenew.minBalanceUsd,
                amountUsd: operation.autoRenew.amountUsd,
              }
            : { enabled: false },
        );
      }

      if (!isCurrentSession(sessionGeneration)) return;
      const refreshedSummary = await showSuccess(amt, sessionGeneration);
      if (!isCurrentSession(sessionGeneration)) return;
      persistCardOperation(null);
      onFundsAdded?.(refreshedSummary);
    } catch (err: any) {
      if (!isCurrentSession(sessionGeneration)) return;
      console.error('Failed to add funds:', err);
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          'Failed to complete payment',
      );
    } finally {
      if (isCurrentSession(sessionGeneration)) setBusy(false);
    }
  };

  const modalTitle = success
    ? 'Funds added'
    : step === 'payment'
      ? 'Add funds'
      : 'Options & confirm';

  const chargeTarget =
    cardOperation?.mode === 'saved_card' || usingSavedCard
      ? cardLabel(savedCard)
      : 'the card you entered';
  const displayedAmountUsd = cardOperation?.amountUsd ?? parsedAmount() ?? 0;

  const requestClose = () => {
    if (!busy && !creditBusy) {
      persistCardOperation(null);
      onClose();
    }
  };

  const handleTeamLinkClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (busy || creditBusy) {
      event.preventDefault();
      return;
    }
    persistCardOperation(null);
    onClose();
  };

  // Shown on both steps: a row naming the team the funds land in, with the
  // team name linking to its billing page (closing the modal on navigate).
  // Step 2 renders it as a fixed, checked, uneditable checkbox; step 1 as
  // plain text.
  const renderTeamLine = (withCheckbox: boolean) => (
    <div className="billing-checkbox-label add-funds-team-line">
      {withCheckbox && (
        <input
          type="checkbox"
          checked
          disabled
          readOnly
          data-testid="add-funds-team-check"
        />
      )}
      <span>
        Funds will be added to{' '}
        <Link
          to={`/console/team/${teamId}/billing`}
          data-testid="add-funds-team-link"
          aria-disabled={busy || creditBusy || undefined}
          onClick={handleTeamLinkClick}>
          {teamName || `Team ${teamId}`}
        </Link>
      </span>
    </div>
  );

  const selectMethod = (nextMethod: FundingMethod) => {
    if (creditBusy || cardOperation) return;
    setMethod(nextMethod);
    setStep('payment');
    setError(null);
    enteredCardRef.current = null;
  };

  const renderMethodChoice = () => (
    <fieldset
      style={{ border: 0, padding: 0, margin: '0 0 16px', minWidth: 0 }}>
      <legend
        style={{
          marginBottom: 6,
          color: 'var(--secondary-text-color)',
          fontSize: '0.8rem',
        }}>
        Add funds with
      </legend>
      <div
        role="radiogroup"
        aria-label="Funding method"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          border: '1px solid var(--border-color)',
          borderRadius: 'var(--radius-sm)',
          overflow: 'hidden',
        }}>
        {(
          [
            ['card', 'Card', 'Pay by card'],
            ['credit', 'Credit code', 'Use credit code'],
          ] as const
        ).map(([value, label, ariaLabel], index) => {
          const selected = method === value;
          return (
            <label
              key={value}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                minWidth: 0,
                padding: '8px 10px',
                borderLeft:
                  index === 0 ? undefined : '1px solid var(--border-color)',
                background: selected
                  ? 'var(--accent-tint)'
                  : 'var(--background-color)',
                color: selected
                  ? 'var(--accent-tint-text)'
                  : 'var(--text-color)',
                fontWeight: selected ? 600 : 400,
                cursor: creditBusy || cardOperation ? 'not-allowed' : 'pointer',
              }}>
              <input
                type="radio"
                name="add-funds-method"
                value={value}
                checked={selected}
                disabled={creditBusy || !!cardOperation}
                aria-label={ariaLabel}
                data-testid={`add-funds-method-${value}`}
                onChange={() => selectMethod(value)}
              />
              <span>{label}</span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={requestClose}
      dismissible={!busy && !creditBusy}
      title={modalTitle}
      isBusy={busy || creditBusy}>
      {loadError ? (
        <div className="projects-error" data-testid="add-funds-load-error">
          {loadError}
        </div>
      ) : !summary ? (
        <div className="billing-modal-loading" aria-label="Loading" />
      ) : success ? (
        <div className="billing-prepay-success" data-testid="add-funds-success">
          <FaCheckCircle className="billing-prepay-success-icon" />
          <p className="billing-prepay-success-amount">
            {formatCostTotal(success.amountUsd)} added to the team balance.
          </p>
          {success.balanceUsd != null && (
            <p
              className="descriptionText"
              data-testid="add-funds-success-balance">
              New balance: {formatCostTotal(success.balanceUsd)}
            </p>
          )}
          <button
            className="primaryButton"
            data-testid="add-funds-done"
            onClick={requestClose}>
            Done
          </button>
        </div>
      ) : (
        <>
          {step === 'payment' && renderMethodChoice()}
          {method === 'card' ? (
            <>
              {/* Step 1 — Payment. Kept mounted (hidden in step 2) so a Stripe
                  card element entered here can still be charged at confirm. */}
              <div
                style={step === 'payment' ? undefined : { display: 'none' }}
                aria-hidden={step !== 'payment'}>
                {renderTeamLine(false)}
                <label className="add-funds-amount-label">
                  Amount to add
                  <DollarAmountInput
                    value={amount}
                    onChange={setAmount}
                    disabled={!!cardOperation}
                    min={minChargeUsd}
                    max={10000}
                    testId="add-funds-amount"
                  />
                </label>

                {hasSavedCard && (
                  <div
                    className="billing-prepay-source"
                    role="radiogroup"
                    aria-label="Payment source">
                    <label className="billing-checkbox-label">
                      <input
                        type="radio"
                        name="add-funds-source"
                        checked={!useNewCard}
                        disabled={!!cardOperation}
                        data-testid="add-funds-use-saved"
                        onChange={() => setUseNewCard(false)}
                      />
                      Use saved card ({cardLabel(savedCard)})
                    </label>
                    <label className="billing-checkbox-label">
                      <input
                        type="radio"
                        name="add-funds-source"
                        checked={useNewCard}
                        disabled={!!cardOperation}
                        data-testid="add-funds-use-new"
                        onChange={() => setUseNewCard(true)}
                      />
                      Use a different card
                    </label>
                  </div>
                )}

                {useNewCard ? (
                  isStripe ? (
                    <StripeCardForm
                      getStripe={getStripe}
                      submitLabel="Continue"
                      busy={busy}
                      error={step === 'payment' ? error : null}
                      onSubmit={onNewCardContinue}
                      onCancel={requestClose}
                    />
                  ) : (
                    <MockCardForm
                      submitLabel="Continue"
                      busy={busy}
                      error={step === 'payment' ? error : null}
                      onSubmit={advanceToOptions}
                      onCancel={requestClose}
                    />
                  )
                ) : (
                  <>
                    {step === 'payment' && error && (
                      <div
                        className="projects-error"
                        data-testid="add-funds-error">
                        {error}
                      </div>
                    )}
                    <div className="billing-card-form-actions">
                      <button
                        className="lightButton"
                        data-testid="add-funds-cancel"
                        onClick={requestClose}>
                        Cancel
                      </button>
                      <button
                        className="primaryButton"
                        data-testid="add-funds-continue"
                        onClick={advanceToOptions}>
                        Continue
                      </button>
                    </div>
                  </>
                )}
              </div>

              {/* Step 2 — Options & confirm */}
              {step === 'options' && (
                <div
                  className="add-funds-options"
                  data-testid="add-funds-options">
                  {renderTeamLine(true)}
                  {useNewCard && (
                    <label className="billing-checkbox-label">
                      <input
                        type="checkbox"
                        checked={saveCard || autoRenew}
                        disabled={autoRenew || !!cardOperation}
                        data-testid="add-funds-save-card"
                        onChange={(e) => setSaveCard(e.target.checked)}
                      />
                      Save this card for future charges
                    </label>
                  )}

                  <label className="billing-checkbox-label">
                    <input
                      type="checkbox"
                      checked={autoRenew}
                      disabled={cardOperation?.mode === 'one_off'}
                      data-testid="add-funds-autorenew"
                      onChange={(e) => {
                        setAutoRenew(e.target.checked);
                        if (e.target.checked && useNewCard) setSaveCard(true);
                      }}
                    />
                    Auto-renew when the balance runs low
                  </label>

                  {autoRenew && (
                    <div className="billing-inline-form add-funds-autorenew-fields">
                      <label>
                        When balance hits (USD)
                        <input
                          type="number"
                          min="0"
                          max="10000"
                          step="0.01"
                          value={autoRenewMin}
                          disabled={cardOperation?.mode === 'one_off'}
                          data-testid="add-funds-autorenew-min"
                          onChange={(e) => setAutoRenewMin(e.target.value)}
                        />
                      </label>
                      <label>
                        Add (USD)
                        <input
                          type="number"
                          min="0.01"
                          max="10000"
                          step="0.01"
                          value={autoRenewAmount}
                          disabled={cardOperation?.mode === 'one_off'}
                          data-testid="add-funds-autorenew-amount"
                          onChange={(e) => setAutoRenewAmount(e.target.value)}
                        />
                      </label>
                    </div>
                  )}

                  <p className="billing-prepay-confirm-text">
                    Charging {formatCostTotal(displayedAmountUsd)} to{' '}
                    {chargeTarget}.
                  </p>

                  {error && (
                    <div
                      className="projects-error"
                      data-testid="add-funds-error">
                      {error}
                      {onRequestLimitIncrease &&
                        error.includes(CHARGE_LIMIT_ERROR_MARKER) && (
                          <p>
                            <button
                              type="button"
                              className="lightButton"
                              data-testid="add-funds-request-increase"
                              onClick={onRequestLimitIncrease}>
                              Request a spending limit increase
                            </button>
                          </p>
                        )}
                    </div>
                  )}

                  {cardOperation?.paymentId && (
                    <div
                      className="descriptionText"
                      data-testid="add-funds-pending"
                      role="status">
                      This payment is still processing. Check it again; you will
                      not be charged twice.
                    </div>
                  )}

                  <div className="delete-modal-actions">
                    <button
                      className="lightButton"
                      data-testid="add-funds-back"
                      disabled={busy}
                      onClick={() => {
                        setError(null);
                        setStep('payment');
                      }}>
                      Back
                    </button>
                    <AsyncButton
                      className="primaryButton"
                      data-testid="add-funds-submit"
                      loading={busy}
                      loadingText="Processing..."
                      onClick={() => billingGuard.run(() => void submit())}>
                      {cardOperation?.paymentId
                        ? 'Check payment'
                        : `Add ${formatCostTotal(displayedAmountUsd)}`}
                    </AsyncButton>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div>
              {renderTeamLine(false)}
              <CreditCodeRedemptionForm
                teamId={teamIdNum}
                onBusyChange={setCreditBusy}
                onRedeemed={(redemption: CreditRedemption) => {
                  setSuccess({
                    amountUsd: redemption.amountUsd,
                    balanceUsd: redemption.balanceUsd,
                  });
                  onFundsAdded?.();
                }}
              />
            </div>
          )}
        </>
      )}
      {billingGuard.denialModal}
    </Modal>
  );
};

export default AddFundsModal;
